import { useCallback, useState } from 'react';
import { useAppSelector } from '../../../../contexts/hooks';
import { NoDataPanel } from '../../..';
import { formatMessage } from 'devextreme/localization';
import TextArea from 'devextreme-react/text-area';
import Toolbar, { Item } from 'devextreme-react/toolbar';
import Button from 'devextreme-react/button';
import { formatDate } from 'devextreme/localization';
import Validator, { RequiredRule } from 'devextreme-react/validator';
import ValidationGroup from 'devextreme-react/validation-group';
import { Notas } from '../../../../types/types.orders';
import { useApiOrders } from '../../../../api/api.orders';
import { LoadIndicator, ScrollView, SelectBox } from 'devextreme-react';
import ContextMenu from 'devextreme-react/context-menu';
import { TypeNotasPlantillas } from '../../../../types/types.config';
import { useScreenSize } from '../../../../utils/media-query';
import ConfirmationPopup from '../../../utils/popup-confirmation/PopupConfirmation';

const Card = ({ note, dateFormat, allowDelete, allowEdit }: { note: Notas; dateFormat: string; allowDelete: boolean; allowEdit: boolean }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState(note.Nor_Nota);
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { updateNote, deleteNote } = useApiOrders();
  const handleSave = async () => {
    setLoading(true);
    const success = await updateNote(note.Nor_Id, editedText);
    if (success) {
      setIsEditing(false);
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    const success = await deleteNote(note.Nor_Id);
    if (success) {
      setShowDeleteConfirm(false);
    }
    setLoading(false);
  };
  // Opciones para el menú contextual
  const menuItems = [
    ...(allowEdit && !isEditing ? [{ text: 'Editar', icon: 'edit', onClick: () => setIsEditing(true) }] : []),
    ...(allowDelete && !isEditing ? [{ text: 'Eliminar', icon: 'trash', onClick: () => setShowDeleteConfirm(true) }] : [])
  ];

  const handleMenuItemClick = (e) => {
    if (e.itemData.onClick) {
      e.itemData.onClick();
    }
  };

  return (
    <>
      <div className='note dx-card' style={{ position: 'relative' }}>
        <div className='note-title'>
          <div>
            {formatDate(new Date(note.Nor_Fecha), dateFormat)} - {note.Usuarios.Us_Nombre} {note.Usuarios.Us_Apellido1} {note.Usuarios.Us_Apellido2}
          </div>          <div style={{ display: 'flex', gap: '8px' }}>
            {!isEditing && menuItems.length > 0 && (
              <>
                <Button
                  id={`note-menu-${note.Nor_Id}`}
                  icon='more'
                  stylingMode='text'
                />
                <ContextMenu
                  target={`#note-menu-${note.Nor_Id}`}
                  dataSource={menuItems}
                  width='auto'
                  showEvent='dxclick'
                  onItemClick={handleMenuItemClick}
                />
              </>
            )}
            {isEditing && (
              <>
                <Button icon='save' disabled={loading} stylingMode='text' onClick={handleSave} />
                <Button icon='close' disabled={loading} stylingMode='text' onClick={() => {
                  setIsEditing(false);
                  setEditedText(note.Nor_Nota);
                }} />
              </>
            )}
          </div>
        </div>
        <div className='note-text'>
          {isEditing ? (
            <TextArea
              value={editedText}
              onValueChange={setEditedText}
              height={100}
              maxLength={500}
              readOnly={loading}
              stylingMode='outlined'
            />
          ) : (
            <div style={{ whiteSpace: 'pre-line' }}>{note.Nor_Nota}</div>
          )}
        </div>        {loading && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}>
            <LoadIndicator width={20} height={20} />
          </div>
        )}
      </div>

      <ConfirmationPopup
        visible={showDeleteConfirm}
        message="¿Está seguro que desea eliminar esta nota?"
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        loading={loading}
      />
    </>
  );
};

const Index = () => {
  const [noteText, setNoteText] = useState('');
  const [loading, setLoading] = useState<boolean>(false);
  const { newNote } = useApiOrders();
  const { isSmall, isXSmall } = useScreenSize();
  const idOrd = useAppSelector((x) => x.globalOrders.CurrentOrder.IdOrder);
  const data = useAppSelector((x) => x.globalOrders.CurrentOrder.Notes);
  const formatDate = useAppSelector((x) => x.configuration.Data?.Config_General.find((x) => x.Cgn_Nombre === 'DateTimeFormat')?.Cgn_Resultado);
  const { Prm_Ord_NotasD, Prm_Ord_NotasC, Prm_Ord_NotasR, Prm_Ord_NotasU } = useAppSelector((x) => (x.userInfo.Permissions ? x.userInfo.Permissions : { Prm_Ord_NotasD: false, Prm_Ord_NotasC: false, Prm_Ord_NotasU: false, Prm_Ord_NotasR: false }));
  const dataCmb = useAppSelector((x) => x.configuration.Data?.NotasPlantillas?.filter((y) => y.Pln_Tipo === TypeNotasPlantillas.Internals)) || [];

  const add = (e) => {
    if (!e.validationGroup.validate().isValid) {
      return;
    }
    setLoading(true);
    newNote(noteText).finally(() => {
      setLoading(false);
    });
    e.validationGroup.reset();
  };

  const onNoteTextChanged = useCallback((value) => {
    setNoteText(value);
  }, []);

  const handleTemplateChange = (e) => {
    setNoteText(noteText ? noteText + '\n' + e.selectedItem['Pln_Notas'] : e.selectedItem['Pln_Notas']);
  };

  if (!idOrd || idOrd === -1) {
    return <NoDataPanel title={formatMessage('OrdNoDataTitle')} description={formatMessage('OrdNoDataDescription')} />;
  }

  return (
    <ScrollView height='calc(100vh - 300px)' style={{ paddingBottom: isSmall || isXSmall ? `150px` : `50px` }}>
      <div className='notes'>
        <ValidationGroup>
          <div className='input-notes'>
            {dataCmb.length > 0 && <SelectBox searchEnabled onSelectionChanged={handleTemplateChange} disabled={!Prm_Ord_NotasC} dataSource={dataCmb} label='Plantilla' displayExpr={Object.keys(dataCmb[0])[1]} valueExpr={Object.keys(dataCmb[0])[0]} labelMode='static' stylingMode='outlined' />}
            <TextArea readOnly={!Prm_Ord_NotasC} label={formatMessage('OrdNewNote')} maxLength={500} stylingMode='outlined' value={noteText} valueChangeEvent='input' onValueChange={onNoteTextChanged}>
              <Validator>
                <RequiredRule />
              </Validator>
            </TextArea>
            <Toolbar>
              <Item
                disabled={loading}
                location='after'
                widget='dxButton'
                options={{
                  disabled: !Prm_Ord_NotasC,
                  text: formatMessage('toolBarNew'),
                  stylingMode: 'outlined',
                  type: 'default',
                  onClick: add,
                }}
              />
              <Item
                location='after'
                widget='dxButton'
                render={() => {
                  return <LoadIndicator visible={loading} width={25} height={25} />;
                }}
              />
            </Toolbar>
          </div>
          <div className='messages-content'>
            <div className='message-list'>{Prm_Ord_NotasR && data && [...data].sort((a, b) => b.Nor_Id - a.Nor_Id).map((note, index) => <Card key={index} note={note} dateFormat={formatDate || 'yyyy/MM/dd hh:mm aa'} allowDelete={Prm_Ord_NotasD} allowEdit={Prm_Ord_NotasU} />)}</div>
          </div>
        </ValidationGroup>
      </div>
      
    </ScrollView>
  );
};

export default Index;
